import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ShieldCheck, X, Award, Loader2, Calendar, MapPin } from "lucide-react";

export default function CertificateVerify() {
  const { certId } = useParams();
  const [cert, setCert] = useState(null);
  const [busy, setBusy] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/academy/certificates/${certId}`)
      .then((r) => {
        setCert(r.data);
        document.title = `${r.data.user_name} · ${r.data.course_title} — Certificat geoVINUM`;
      })
      .catch(() => setNotFound(true))
      .finally(() => setBusy(false));
  }, [certId]);

  if (busy) return <div className="max-w-2xl mx-auto px-5 py-24 text-center text-[#8B95A1]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" />Vérification…</div>;

  if (notFound || !cert) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-24" data-testid="cert-verify-notfound">
        <div className="app-card p-8 text-center border-l-4 border-red-500">
          <X className="w-12 h-12 text-red-500 mx-auto" />
          <h1 className="font-display text-3xl text-[#1F3A5F] mt-4">Certificat invalide</h1>
          <p className="text-[#4A5568] mt-2">Ce certificat n&apos;existe pas dans notre base ou l&apos;ID est incorrect.</p>
          <Link to="/courses" className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-[#E5276A] text-white text-sm font-bold">
            Découvrir Academy
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-5 py-16 md:py-24" data-testid="cert-verify-ok">
      <div className="app-card p-8 md:p-10 border-t-4 border-emerald-500 text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
          <ShieldCheck className="w-9 h-9 text-emerald-600" />
        </div>
        <div className="mt-3 text-[10px] uppercase tracking-widest text-emerald-700 font-bold">
          Certificat authentifié
        </div>
        <h1 className="font-display text-3xl md:text-4xl text-[#1F3A5F] mt-4 font-medium">
          {cert.user_name}
        </h1>
        <div className="text-sm text-[#4A5568] mt-1">a validé le parcours</div>
        <div className="font-display text-2xl text-[#E5276A] mt-3">{cert.course_title}</div>

        <div className="mt-6 grid grid-cols-3 gap-3 text-center">
          <div className="rounded-xl bg-[#FBF7F3] p-3">
            <Award className="w-4 h-4 text-[#1F3A5F] mx-auto" />
            <div className="text-[9px] uppercase tracking-wider text-[#8B95A1] mt-1 font-bold">Niveau</div>
            <div className="text-sm font-bold text-[#1F3A5F] mt-0.5">{cert.course_level_label}</div>
          </div>
          <div className="rounded-xl bg-[#FBF7F3] p-3">
            <MapPin className="w-4 h-4 text-[#E5276A] mx-auto" />
            <div className="text-[9px] uppercase tracking-wider text-[#8B95A1] mt-1 font-bold">Région</div>
            <div className="text-sm font-bold text-[#1F3A5F] mt-0.5">{cert.region}</div>
          </div>
          <div className="rounded-xl bg-[#FBF7F3] p-3">
            <Calendar className="w-4 h-4 text-[#1F3A5F] mx-auto" />
            <div className="text-[9px] uppercase tracking-wider text-[#8B95A1] mt-1 font-bold">Délivré</div>
            <div className="text-sm font-bold text-[#1F3A5F] mt-0.5">{cert.issued_at?.slice(0, 10)}</div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-[#1F3A5F]/8">
          <div className="text-[10px] uppercase tracking-wider text-[#8B95A1]">Score final</div>
          <div className="font-display text-4xl text-[#1F3A5F] mt-1">{cert.final_score}%</div>
        </div>

        <p className="text-[10px] text-[#8B95A1] mt-6 tracking-widest">ID · {cert.id}</p>
      </div>

      <p className="text-xs text-[#8B95A1] mt-6 text-center">
        Ce certificat a été délivré par geoVINUM Academy et est infalsifiable.
      </p>
      <div className="mt-4 text-center">
        <Link to="/courses" className="text-xs text-[#E5276A] hover:underline font-bold">
          Découvrir les parcours geoVINUM Academy →
        </Link>
      </div>
    </div>
  );
}
